import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'

interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
  placeholder?: string
  className?: string
}

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
}

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'blockquote',
  'code-block',
  'link',
]

export function RichTextEditor({ content, onChange, placeholder, className }: RichTextEditorProps) {
  return (
    <div className={`rich-text-editor ${className || ''}`}>
      <ReactQuill
        theme="snow"
        value={content}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder || 'Start typing...'}
      />
      <style>{`
        .rich-text-editor .ql-container {
          min-height: 150px;
          font-size: 14px;
          font-family: inherit;
        }
        .rich-text-editor .ql-editor {
          min-height: 150px;
        }
        .rich-text-editor .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          background: var(--rs-color-surface-2);
          border-color: var(--rs-color-border-strong);
        }
        .rich-text-editor .ql-container {
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
          border-color: var(--rs-color-border-strong);
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: var(--rs-color-text-2);
          font-style: normal;
        }
        .rich-text-editor:focus-within .ql-toolbar,
        .rich-text-editor:focus-within .ql-container {
          border-color: var(--rs-color-border-focus);
        }
      `}</style>
    </div>
  )
}

// Component to render HTML content safely
interface RichTextContentProps {
  content: string
  className?: string
}

export function RichTextContent({ content, className }: RichTextContentProps) {
  return (
    <>
      <div 
        className={`rich-text-content prose prose-sm max-w-none overflow-hidden ${className || ''}`}
        dangerouslySetInnerHTML={{ __html: content }}
      />
      <style>{`
        .rich-text-content {
          word-wrap: break-word;
          overflow-wrap: anywhere;
          word-break: normal;
          hyphens: auto;
        }
        .rich-text-content ul,
        .rich-text-content ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .rich-text-content ul {
          list-style-type: disc;
        }
        .rich-text-content ol {
          list-style-type: decimal;
        }
        .rich-text-content li {
          margin: 0.25em 0;
        }
        .rich-text-content p {
          margin: 0.5em 0;
        }
        .rich-text-content h1 {
          font-size: 1.5em;
          font-weight: 600;
          margin: 0.75em 0 0.5em;
        }
        .rich-text-content h2 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 0.75em 0 0.5em;
        }
        .rich-text-content h3 {
          font-size: 1.1em;
          font-weight: 600;
          margin: 0.75em 0 0.5em;
        }
        .rich-text-content blockquote {
          border-left: 3px solid var(--rs-color-border);
          padding-left: 1em;
          margin: 0.5em 0;
          color: var(--rs-color-text-2);
        }
        .rich-text-content pre {
          background: var(--rs-color-surface-3);
          padding: 0.75em;
          border-radius: 0.375rem;
          overflow-x: auto;
        }
        .rich-text-content a {
          color: var(--rs-color-text-link);
          text-decoration: underline;
        }
        .rich-text-content strong {
          font-weight: 600;
        }
      `}</style>
    </>
  )
}
